import React, { useEffect, useMemo, useRef, useState } from 'react';
import { dashboardAPI } from '../../services';
import './styles/dashboard.css';

const formatAmount = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  try {
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch {
    return value;
  }
};

const formatDueDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const TopCustomers = ({ type }) => {
  const isPurchase = type === 'purchase';
  const title = isPurchase ? 'Top 5 Customers by Purchase Value' : 'Top 5 Credit Customers';
  const subtitle = isPurchase ? 'Highest spending customers this month' : 'Customers with highest pending credits';

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchedPurchase = useRef(false);
  const fetchedCredit = useRef(false);

  const statusStyle = useMemo(
    () => ({
      Active: { color: '#027A48', bg: '#ECFDF3' },
      Credit: { color: '#B54708', bg: '#FFFAEB' },
      Paid: { color: '#175CD3', bg: '#EFF8FF' },
      Pending: { color: '#B54708', bg: '#FFFAEB' },
      'Reminder Sent': { color: '#B42318', bg: '#FEF3F2' },
      default: { color: '#344054', bg: '#F2F4F7' },
    }),
    []
  );

  const getStatusStyle = (status) => statusStyle[status] || statusStyle.default;

  const getAvatarStyle = () =>
    !isPurchase
      ? { bg: '#FFFAEB', color: '#B54708' }
      : { bg: '#F0F9FF', color: '#026AA2' };

  const fetchPurchase = async () => {
    if (fetchedPurchase.current) return;
    fetchedPurchase.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardAPI.getTopPurchaseCustomers();
      const list = data?.data || data?.results || data || [];
      const normalized = (Array.isArray(list) ? list : []).slice(0, 5).map((item) => ({
        name: item?.customer_name || item?.name || '--',
        detail: item?.total_orders_in_month ? `${item.total_orders_in_month} orders` : (item?.customer_email || item?.customer_phone || ''),
        amount: item?.total_purchase_value ?? item?.purchase_value ?? item?.amount ?? 0,
        status: item?.status || 'Active',
      }));
      setCustomers(normalized);
    } catch (err) {
      console.error('Error fetching top purchase customers:', err);
      setError('Could not load purchase customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCredit = async () => {
    if (fetchedCredit.current) return;
    fetchedCredit.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardAPI.getTopCreditCustomers();
      const list = data?.data || data?.results || data || [];
      const normalized = (Array.isArray(list) ? list : []).slice(0, 5).map((item) => ({
        name: item?.customer_name || item?.name || '--',
        detail: item?.last_due_date ? `Due: ${formatDueDate(item.last_due_date)}` : (item?.due_date ? `Due: ${formatDueDate(item.due_date)}` : ''),
        amount: item?.credit_amount ?? item?.pending_credit ?? item?.amount ?? item?.balance ?? 0,
        status: item?.status || 'Pending',
      }));
      setCustomers(normalized);
    } catch (err) {
      console.error('Error fetching top credit customers:', err);
      setError('Could not load credit customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPurchase) {
      fetchPurchase();
    } else {
      fetchCredit();
    }
    // we intentionally exclude fetch functions to avoid re-creating
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPurchase]);

  return (
    <div className="dashboard-card" style={{ height: '502px', borderRadius: '10px' }}>
      <h3 className="dashboard-card-title">{title}</h3>
      <p className="dashboard-subtitle" style={{ marginBottom: '20px' }}>{subtitle}</p>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {loading && <p style={{ color: '#6B7280', fontSize: '13px' }}>Loading...</p>}
        {error && <p style={{ color: '#EF4444', fontSize: '13px' }}>{error}</p>}
        {!loading && !error && customers.map((customer, index) => {
          const style = getStatusStyle(customer.status);
          const avatarStyle = getAvatarStyle();

          return (
            <div key={index} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '15px 0',
              borderBottom: index !== customers.length - 1 ? '1px solid #F3F4F6' : 'none'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: avatarStyle.bg,
                  color: avatarStyle.color,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {customer.name ? customer.name.split(' ').map(n => n[0]).join('') : '--'}
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#101828', margin: 0, marginBottom: '2px' }}>{customer.name || '--'}</p>
                  <p style={{ fontSize: '13px', color: '#667085', margin: 0 }}>{customer.detail || ''}</p>
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: '14px', fontWeight: '700', color: '#101828', margin: 0, marginBottom: '4px' }}>
                  {`€ ${formatAmount(customer.amount)}`}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TopCustomers;
